import React, { Component } from 'react';
import Axios from 'axios';
import classnames from 'classnames';
import { FaFileExcel, FaFillDrip, FaCouch, FaPlus, FaSun } from 'react-icons/fa';
import { Dropdown, MenuItem } from 'react-bootstrap';

import intlShape from '@/shared/utils/intlShape';
import SimplePanel from '@/shared/components/SimplePanel';
import { FormattedMessage, injectIntl } from 'react-intl';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import moment from 'moment';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import { showWhenPermission } from '@/shared/components/security';
import Button from '@/shared/components/Button';
import handleHttpError from '@/shared/services/HandleHttpError';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import AddRegistrationDropdownButton from './AddRegistrationDropdownButton';
import { CanCreate } from '@/shared/components/security';
import AuthService, { functionalities } from '@/shared/services/AuthService';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import {
  canCreateRegistrationForUser,
  getDefaultRegistrationOptionsForType,
} from '@/shared/components/registration/registrationHelpers';
import { getResourceName } from '@/shared/components/ResourceName';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import FillGapsRegistrationModal from '@/shared/components/registration/FillGapsRegistrationModal';
import HoverToggle from '@/shared/components/HoverToggle';
import { injectStateService, stateServicePropType } from '@/shared/services/StateService';
import dateFormatService from '@/shared/services/DateFormatService';
import HoursPerEmployeeReportHomeWorkingRow from './HoursPerEmployeeReportHomeWorkingRow';
import messages from '../reportingMessages';
import HoursPerEmployeeReportFilters, { defaultFilters } from './HoursPerEmployeeReportFilters';
import HoursPerEmployeeReportDetailRow from './HoursPerEmployeeReportDetailRow';
import HoursPerEmployeeReportCell from './HoursPerEmployeeReportCell';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import Checkbox from '@/shared/components/input/Checkbox';
import GlobalActionsButton from './GlobalActionsButton';
import BatchFillGapsRegistrationModal from '@/modules/reporting/hoursPerEmployee/BatchFillGapsRegistrationModal';
import ExportToExcelDropdown from '@/modules/reporting/registrationsPer/ExportToExcelDropdown';

const SessionStorageKey = 'HoursPerEmployeeReportParams';

const resourceTypesForDetailType = {
  1: [0], // Work
  2: [5], // Training
  3: [3], // Absence
  4: [4], // Recup
};

class HoursPerEmployeeReportContainer extends Component {
  state = {
    reportFilters: {},
    report: null,
    showDetails: {},
    showResources: {},
    resources: {},
    showRegistrationModal: null,
    showFillGapsRegistrationModal: null,
    showBatchFillGapsRegistrationModal: null,
    allSelected: false,
    exporting: false,
  };

  constructor(props) {
    super(props);
    const { searchParams = {} } = props;
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment(sessionParams.dateUntil);
    }
    this.hasUpdateUsersPermission = AuthService.hasPermission({
      functionality: 'Users',
      privilege: 'Update',
    });
    this.currentUserId = AuthService.getUserId();

    this.state = {
      ...this.state,
      reportFilters: {
        ...defaultFilters,
        ...sessionParams,
        ...searchParams,
      },
    };
  }

  componentDidMount() {
    this.retrieve();
    SignalRService.subscribe('yearTimeTableUpdated', this.handleYearTimeTableUpdatedEvent);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('yearTimeTableUpdated', this.handleYearTimeTableUpdatedEvent);
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  getParams(includeCompanies = true) {
    const { reportFilters } = this.state;
    const {
      groupBy,
      dateFrom: from,
      dateUntil: until,
      ownCompanies,
      legalEntities,
      userId = null,
      resourceTypes,
      teamCodes,
      employeeType,
      onlyWithMissingHours,
      withEmployeeLabels,
    } = reportFilters;

    return {
      groupBy,
      from,
      until,
      ...(includeCompanies && { ownCompanies }),
      legalEntities,
      userId,
      resourceTypes,
      teamCodes,
      employeeType,
      onlyWithMissingHours,
      withEmployeeLabels,
    };
  }

  retrieve() {
    const { reportFilters } = this.state;
    const { intl } = this.props;

    const params = this.getParams();
    if (!params.from) {
      ToastrService.error(intl.formatMessage(messages.DateFromRequired));
      return;
    }

    const sessionParams = JSON.stringify(reportFilters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);

    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();

    return api.base
      .post(`${config.appUrls.reporting}/api/registeredhoursperemployee`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data: { columns = [], rows = [], total = 0, totalRemainingOvertime = 0 } = {} }) => {
        this.setState(({ allSelected }) => ({
          report: {
            columns: columns.map(({ startDate, endDate, ...col }) => ({
              ...col,
              startDate: moment.utc(startDate),
              endDate: moment.utc(endDate),
              name: col.name.replace('/', dateFormatService.delimiter()),
            })),
            rows: rows.map(x => ({ ...x, selected: allSelected })),
            total,
            totalRemainingOvertime,
          },
          showResources: {},
          resources: {},
        }));
        return true;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          ToastrService.error();
        }
      });
  }

  handleYearTimeTableUpdatedEvent = eventParams => {
    const { userId, dateFrom, dateUntil } = eventParams;
    if (!userId) return;
    const { report, reportFilters } = this.state;
    if (moment.utc(dateUntil).isBefore(moment.utc(reportFilters.dateFrom))) return;
    if (!reportFilters.dateUntil) {
      if (moment.utc(dateFrom).isAfter(moment.utc(reportFilters.dateFrom).add(1, 'month'))) return;
    } else if (moment.utc(dateFrom).isAfter(moment.utc(reportFilters.dateUntil))) return;
    if (!report || !report.rows || report.rows.findIndex(r => r.userId === userId) < 0) return;

    this.retrieve();
  };

  createExcelReport = (isDetailsReport = false) => {
    debugger;
      const { intl } = this.props;
  
      const reportParams = { ...this.getParams(), isDetailsReport };
      const languageCode = intl.locale;
  
      const params = {
        language: languageCode,
        signalRConnectionId: SignalRService.getConnectionId(),
        report: 2,
        parameters: JSON.stringify(reportParams),
      };
      this.setState({ exporting: true });
      return api.base
        .post(`${config.appUrls.reporting}/api/export`, params)
        .then(() => {
          ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
        .catch(error => {
          handleHttpError(intl)(error);
      })
        .finally(() => {
          this.setState({ exporting: false });
      });
    };

  updateHomeWorkingHours = (userId, date, value) => {
    const params = {
      userId,
      date,
      homeWorkingMinutes: value,
    };
    return api.base
      .post(`${config.appUrls.api}/api/Employee/HomeWorking`, params)
      .then(({ data }) => {
        this.setState(({ report }) => {
          const col = report.columns.find(x => x.startDate === date);

          return {
            report: {
              ...report,
              rows: report.rows.map(x => {
                if (x.userId !== userId) return x;

                const homeWorking = { ...x.homeWorking, [col.groupNr]: data };
                if (data === 0) delete homeWorking[col.groupNr];
                return {
                  ...x,
                  homeWorking,
                  totalHomeWorking: Object.keys(homeWorking).reduce(
                    (sum, key) => sum + (homeWorking[key] || 0),
                    0,
                  ),
                };
              }),
            },
          };
        });

        // if (data === params.HomeWorkingMinutes) this.retrieve();
      });
  };

  filtersChanged(filters) {
    this.setState(
      state => ({
        ...state,
        reportFilters: {
          ...state.reportFilters,
          ...filters,
        },
        report: null,
      }),
      () => this.retrieve(),
    );
  }
  handleSelect = row => {
    this.setState(({ report }) => ({
      report: {
        ...report,
        rows: report.rows.map(x => (x.userId === row.userId ? { ...x, selected: !x.selected } : x)),
      },
    }));
  };
  handleSelectAll = () => {
    this.setState(({ report, allSelected }) => ({
      report: { ...report, rows: report.rows.map(x => ({ ...x, selected: !allSelected })) },
      allSelected: !allSelected,
    }));
  };
  isGlobalActionsDisabled = () => {
    const { report, allSelected } = this.state;
    if (!report?.rows || !report.rows.length) return true;
    if (report.rows.some(x => x.selected) || allSelected) return false;

    return true;
  };
  getSelectedUserIds = () => {
    const { report, allSelected } = this.state;

    const userIds = report.rows
      ? report.rows.filter(x => x.selected || false).map(x => x.userId)
      : [];
    return userIds;
  };

  renderMonth = date => {
    const { intl } = this.props;
    const month = date.month() + 1; // moment month is 0-index based
    return `${intl.formatMessage(listItemMsg('month', month))} ${date.year()}`;
  };

  renderHeaderLabel = (column, groupBy) => {
    switch (groupBy) {
      case 3:
        return this.renderMonth(column.startDate);
      default:
        return column.name;
    }
  };
  toggleDetails = ({ userId, hasDetails }, event) => {
    if (!hasDetails) return;
    if (event.defaultPrevented) return; // Probably dropdown
    this.setState(({ showDetails }) => ({
      showDetails: { ...showDetails, [userId]: !showDetails[userId] },
    }));
  };
  getDateRange = () => {
    const { reportFilters, report } = this.state;
    let { dateFrom, dateUntil } = reportFilters;
    if (!dateFrom && report && report.columns) {
      const [firstCol] = report.columns;
      if (firstCol) {
        dateFrom = moment.utc(firstCol.endDate);
      }
    }
    if (!dateUntil && report && report.columns) {
      const [lastCol] = report.columns.slice(-1);
      if (lastCol) {
        dateUntil = moment.utc(lastCol.endDate);
      }
    }
    return { dateFrom, dateUntil };
  };
  openRegistrationModal = ({ type, userId, date }) => {
    const options = { ...getDefaultRegistrationOptionsForType(type) };
    this.setState({
      showRegistrationModal: {
        registration: { type, userId, date: moment.utc(date).toDate() },
        registrationOptions: options,
      },
    });
  };
  onBatchFillGaps = registrationData => {
    const data = {
      ...registrationData,
      userIds: this.getSelectedUserIds(),
    };

    return api.base.post(`${config.appUrls.api}/api/Registration/BatchFillDays`, data).then(
      ({ data }) => {
        ToastrService.success();
        this.setState({ showBatchFillGapsRegistrationModal: null });
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  openFillGapsRegistrationModal = ({
    type = null,
    userId = null,
    dateFrom: argDateFrom,
    dateUntil: argDateUntil,
    remainingOvertime = null,
  } = {}) => {
    let dateFrom = argDateFrom ? moment.utc(argDateFrom) : null;
    let dateUntil = argDateUntil ? moment.utc(argDateUntil) : null;

    if (!dateFrom && !dateUntil) {
      ({ dateFrom, dateUntil } = this.getDateRange());
      const today = moment.utc().startOf('day');
      if (dateFrom.isBefore(today) && dateUntil.isAfter(today)) dateUntil = today;
    }
    const registration = { type, userId };
    // If there is remaining overtime, set the registration type to recup
    if (type === null && remainingOvertime !== null && remainingOvertime > 0) registration.type = 4;

    this.setState({
      showFillGapsRegistrationModal: {
        registration,
        dateFrom,
        dateUntil,
        userId,
        remainingOvertime,
      },
    });
  };
  openBatchFillGapsRegistrationModal = ({ resourceType = null } = {}) => {
    let { dateFrom, dateUntil } = this.getDateRange();
    const today = moment.utc().startOf('day');
    if (dateFrom.isBefore(today) && dateUntil.isAfter(today)) dateUntil = today;
    this.setState({
      showBatchFillGapsRegistrationModal: {
        resourceType,
        dateFrom,
        dateUntil,
      },
    });
  };

  changeToDayView = (dateFrom, dateUntil) => {
    this.filtersChanged({
      groupBy: 1,
      dateFrom: moment.utc(dateFrom),
      dateUntil: moment.utc(dateUntil),
    });
  };

  toggleResourceDetails = (type, userId) => {
    this.setState(({ showResources, resources }) => {
      const key = `${type};${userId}`;
      const currentValue = !!showResources[key];
      if (!currentValue && typeof resources[key] === 'undefined') {
        this.retrieveResourceDetails(type, userId);
        return {
          showResources: { ...showResources, [key]: !currentValue },
          resources: { ...resources, [key]: null },
        };
      }
      return {
        showResources: { ...showResources, [key]: !currentValue },
      };
    });
  };
  retrieveResourceDetails = (type, userId) => {
    const { intl } = this.props;

    const reportParams = this.getParams(false);
    const params = {
      ...reportParams,
      userId,
      resourceTypes: resourceTypesForDetailType[type],
    };

    return api.base
      .post(`${config.appUrls.reporting}/api/registeredhoursperemployeeperresource`, params)
      .then(({ data }) => {
        const key = `${type};${userId}`;

        const resourceData = (data ? data.rows : null || [])
          .map(x => ({
            ...x,
            label: getResourceName(intl, x),
          }))
          .sort(dynamicSort('label'));

        this.setState(({ resources }) => ({
          resources: { ...resources, [key]: resourceData },
        }));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  renderResourceDetails = (type, userId) => {
    const { resources, showResources, report } = this.state;
    const sharedProps = { userId, columns: report.columns };
    const key = `${type};${userId}`;
    if (!showResources[key]) return [];
    if (!resources[key]) return [];
    if (resources[key].length <= 0) return [];
    return resources[key].map(({ id, label, values, total }) => (
      <HoursPerEmployeeReportDetailRow
        type={99}
        key={`${userId}-99-${id}`}
        values={values}
        total={total}
        label={label}
        {...sharedProps}
      />
    ));
  };

  renderDetails = row => {
    if (!row || !row.hasDetails) return null;
    const { report, showResources, reportFilters } = this.state;
    const { intl } = this.props;
    const sharedProps = { userId: row.userId, columns: report.columns };
    const isCurrentUser = row.userId === this.currentUserId;
    const canEditHomeWorking =
      reportFilters.groupBy === 1 && (isCurrentUser || this.hasUpdateUsersPermission);

    const detailsRows = [
      <HoursPerEmployeeReportDetailRow
        type={0}
        key={`${row.userId}-0`}
        values={row.expected}
        total={row.totalExpected}
        label={intl.formatMessage(messages.ExpectedHours)}
        {...sharedProps}
      />,
      <HoursPerEmployeeReportDetailRow
        type={1}
        key={`${row.userId}-1`}
        values={row.worked}
        total={row.totalWorked}
        label={intl.formatMessage(messages.WorkedHours)}
        toggleResources={() => this.toggleResourceDetails(1, row.userId)}
        showResources={!!showResources[`1;${row.userId}`]}
        {...sharedProps}
      />,
      ...this.renderResourceDetails(1, row.userId),
    ];

    if (row.totalTraining > 0) {
      detailsRows.push(
        <HoursPerEmployeeReportDetailRow
          type={2}
          key={`${row.userId}-2`}
          values={row.training}
          total={row.totalTraining}
          label={intl.formatMessage(messages.TrainingHours)}
          toggleResources={() => this.toggleResourceDetails(2, row.userId)}
          showResources={!!showResources[`2;${row.userId}`]}
          {...sharedProps}
        />,
        ...this.renderResourceDetails(2, row.userId),
      );
    }
    if (row.totalAbsence > 0) {
      detailsRows.push(
        <HoursPerEmployeeReportDetailRow
          type={3}
          key={`${row.userId}-3`}
          values={row.absence}
          total={row.totalAbsence}
          label={intl.formatMessage(messages.AbsenceHours)}
          toggleResources={() => this.toggleResourceDetails(3, row.userId)}
          showResources={!!showResources[`3;${row.userId}`]}
          {...sharedProps}
        />,
        ...this.renderResourceDetails(3, row.userId),
      );
    }
    if (row.totalRecuperation > 0)
      detailsRows.push(
        <HoursPerEmployeeReportDetailRow
          type={4}
          key={`${row.userId}-4`}
          values={row.recuperation}
          total={row.totalRecuperation}
          label={intl.formatMessage(messages.RecuperationHours)}
          toggleResources={() => this.toggleResourceDetails(4, row.userId)}
          showResources={!!showResources[`4;${row.userId}`]}
          {...sharedProps}
        />,
        ...this.renderResourceDetails(4, row.userId),
      );
    // detailsRows.push(
    //   <HoursPerEmployeeReportDetailRow
    //     type={4}
    //     key={`${row.userId}-4`}
    //     values={row.filledIn}
    //     total={row.totalFilledIn}
    //     label={intl.formatMessage(messages.FilledInHours)}
    //     {...sharedProps}
    //   />,
    // );
    // if (row.totalFilledIn !== row.totalCappedFilledIn)
    //   detailsRows.push(
    //     <HoursPerEmployeeReportDetailRow
    //       type={5}
    //       key={`${row.userId}-5`}
    //       values={row.cappedFilledIn}
    //       total={row.totalCappedFilledIn}
    //       label={intl.formatMessage(messages.CappedFilledInHours)}
    //       {...sharedProps}
    //     />,
    //   );
    if (row.totalMissing > 0)
      detailsRows.push(
        <HoursPerEmployeeReportDetailRow
          type={6}
          key={`${row.userId}-6`}
          values={row.missing}
          total={row.totalMissing}
          label={intl.formatMessage(messages.MissingHours)}
          {...sharedProps}
        />,
      );
    if (row.totalOvertime > 0)
      detailsRows.push(
        <HoursPerEmployeeReportDetailRow
          type={7}
          key={`${row.userId}-7`}
          values={row.overtime}
          total={row.totalOvertime}
          label={intl.formatMessage(messages.OvertimeHours)}
          {...sharedProps}
        />,
      );
    if (row.totalOvertimeCorrection !== 0)
      detailsRows.push(
        <HoursPerEmployeeReportDetailRow
          type={8}
          key={`${row.userId}-8`}
          values={row.overtimeCorrection}
          total={row.totalOvertimeCorrection}
          label={intl.formatMessage(messages.OvertimeCorrectionHours)}
          {...sharedProps}
        />,
      );
    if (
      row.totalRemainingOvertimePeriod !== 0 ||
      (row.remainingOvertimePeriod && Object.keys(row.remainingOvertimePeriod).length > 0)
    )
      detailsRows.push(
        <HoursPerEmployeeReportDetailRow
          type={9}
          key={`${row.userId}-9`}
          values={row.remainingOvertimePeriod}
          total={row.totalRemainingOvertimePeriod}
          label={intl.formatMessage(messages.RemainingOvertimeHoursPeriod)}
          {...sharedProps}
        />,
      );

    if (row.totalHomeWorking !== 0 || canEditHomeWorking) {
      if (canEditHomeWorking) {
        // Editable on day view
        detailsRows.push(
          <HoursPerEmployeeReportHomeWorkingRow
            type={10}
            key={`${row.userId}-10`}
            values={row.homeWorking}
            total={row.totalHomeWorking}
            label={intl.formatMessage(messages.HomeWorkingHours)}
            onUpdateHomeWorkingHours={this.updateHomeWorkingHours}
            {...sharedProps}
          />,
        );
      } else {
        detailsRows.push(
          <HoursPerEmployeeReportDetailRow
            type={10}
            key={`${row.userId}-10`}
            values={row.homeWorking}
            total={row.totalHomeWorking}
            label={intl.formatMessage(messages.HomeWorkingHours)}
            {...sharedProps}
          />,
        );
      }
    }
    return detailsRows;
  };

  render() {
    const { intl, stateService } = this.props;
    const {
      reportFilters,
      report,
      showDetails,
      showRegistrationModal,
      showFillGapsRegistrationModal,
      showBatchFillGapsRegistrationModal,
      allSelected,
      exporting
    } = this.state;
    const showRemainingOvertime =
      report && !!report.totalRemainingOvertime && report.totalRemainingOvertime !== 0;

    const totalRemainingOvertimePeriod =
      report && report.rows
        ? report.rows.reduce((sum, row) => sum + (row.totalRemainingOvertimePeriod || 0), 0)
        : 0;

    const dateRange = this.getDateRange();

    return (
      <div className="hours-per-employee">
        <SimplePanel
          article="5434857550738"
          title={intl.formatMessage(messages.HoursPerEmployee)}
          buttons={
            <CanCreate on="RegistrationOwn">
              <AddRegistrationDropdownButton
                onAddRegistration={this.openRegistrationModal}
                userId={reportFilters.userId}
                intl={intl}
              />
            </CanCreate>
          }
        >
          <div className="info-block container-fluid">
            <HoursPerEmployeeReportFilters
              filters={reportFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
            />
          </div>
        </SimplePanel>

        {report && report.rows && !!report.rows.length && (
          <>
            <div className="row btn-row">
              <div className="col-md-6 pull-right content-right">
                <ExportToExcelDropdown
                  exporting={exporting}
                  createExcel={this.createExcelReport}
                  intl={intl}
                />
                <CanCreate on={functionalities.RegistrationOthers}>
                  <GlobalActionsButton
                    onBatchFillGaps={this.openBatchFillGapsRegistrationModal}
                    intl={intl}
                    disabled={this.isGlobalActionsDisabled()}
                  />
                </CanCreate>
              </div>
            </div>

            <div className="hours-per-employee-table-container">
              <div className="hours-per-employee-table-wrapper">
                <table className="table table-report hours-per-employee-table">
                  <thead>
                    <tr>
                      <th className="checkbox-column">
                        <Checkbox
                          extraClass="margin2L"
                          onClick={() => this.handleSelectAll()}
                          value={allSelected}
                        />
                      </th>
                      <th>
                        <FormattedMessage {...messages.Employee} />
                      </th>
                      {report.columns.map(col => (
                        <th key={col.groupNr} className="text-right no-wrap">
                          {this.renderHeaderLabel(col, reportFilters.groupBy)}
                        </th>
                      ))}
                      <th className="text-right">
                        <FormattedMessage {...messages.Total} />
                      </th>
                      {totalRemainingOvertimePeriod !== 0 && (
                        <th className="text-right">
                          <FormattedMessage {...messages.RemainingOvertimeHoursPeriod} />
                        </th>
                      )}
                      {showRemainingOvertime && (
                        <th className="text-right">
                          <FormattedMessage {...messages.RemainingOvertimeHoursAll} />
                        </th>
                      )}
                    </tr>
                  </thead>
                  {report.rows.map(row => {
                    const toggleFunction = e => this.toggleDetails(row, e);
                    const canCreateRegistration = canCreateRegistrationForUser(row.userId);
                    return (
                      <tbody key={row.userId}>
                        <tr>
                          <td>
                            <Checkbox onClick={() => this.handleSelect(row)} value={row.selected} />
                          </td>
                          <td onClick={toggleFunction} className="pointer">
                            {row.hasDetails && (
                              <span className="caret-button">
                                <i
                                  className={classnames('fa', {
                                    'fa-caret-up': !!showDetails[row.userId],
                                    'fa-caret-down': !showDetails[row.userId],
                                  })}
                                />
                              </span>
                            )}
                            {row.name}
                          </td>
                          {report.columns.map(col => {
                            const value = row.values[col.groupNr];
                            const expected = (row.expected && row.expected[col.groupNr]) || 0;
                            const overtime = (row.overtime && row.overtime[col.groupNr]) || 0;
                            const missing = (row.missing && row.missing[col.groupNr]) || 0;
                            const homeWorking =
                              (row.homeWorking && row.homeWorking[col.groupNr]) || 0;
                            return (
                              <HoursPerEmployeeReportCell
                                key={col.groupNr}
                                value={value}
                                expected={expected}
                                overtime={overtime}
                                missing={missing}
                                homeWorking={homeWorking}
                                startDate={col.startDate}
                                endDate={col.endDate}
                                userId={row.userId}
                                groupNr={col.groupNr}
                                groupBy={reportFilters.groupBy}
                                onAddRegistration={this.openRegistrationModal}
                                onToggleDetails={toggleFunction}
                                onChangeToDayView={this.changeToDayView}
                                onUpdateHomeWorkingHours={this.updateHomeWorkingHours}
                                onOpenFillGapsRegistrationModal={type =>
                                  this.openFillGapsRegistrationModal({
                                    userId: row.userId,
                                    dateFrom: col.startDate,
                                    dateUntil: col.endDate,
                                    remainingOvertime: row.remainingOvertime,
                                    type,
                                  })
                                }
                                hasDetails={row.hasDetails}
                                intl={intl}
                                stateService={stateService}
                              />
                            );
                          })}
                          <td
                            className={classnames('text-right hours ', {
                              'text-muted': !row.total,
                            })}
                            onClick={toggleFunction}
                          >
                            <Dropdown id={`Dropdown-${row.userId}-rowActions`} pullRight>
                              <HoverToggle bsRole="toggle" />
                              <span className="info">{row.total.minuteToTime()}</span>

                              <Dropdown.Menu className="unselectable">
                                <MenuItem
                                  eventKey="0"
                                  onSelect={() =>
                                    stateService.gotoRegistrationOverview({
                                      userId: row.userId,
                                      dateFrom: dateRange.dateFrom,
                                      dateUntil: dateRange.dateUntil,
                                      period: 'all',
                                    })
                                  }
                                >
                                  <i className="fa fa-th fa-fw margin5R" />
                                  <FormattedMessage {...messages.OpenRegistrationOverview} />
                                </MenuItem>
                                {canCreateRegistration && row.hasDetails && row.totalMissing > 0 ? (
                                  <MenuItem
                                    eventKey="1"
                                    onSelect={() =>
                                      this.openFillGapsRegistrationModal({
                                        userId: row.userId,
                                        remainingOvertime: row.remainingOvertime,
                                        type: 0,
                                      })
                                    }
                                  >
                                    <FaFillDrip className="react-icon fa-fw margin5R" />
                                    <FormattedMessage {...messages.FillPeriodWithWork} />
                                  </MenuItem>
                                ) : null}
                                {canCreateRegistration && row.hasDetails && row.totalMissing > 0 ? (
                                  <MenuItem
                                    eventKey="2"
                                    onSelect={() =>
                                      this.openFillGapsRegistrationModal({
                                        userId: row.userId,
                                        remainingOvertime: row.remainingOvertime,
                                        type: 4,
                                      })
                                    }
                                  >
                                    <FaFillDrip className="react-icon fa-fw margin5R" />
                                    <FormattedMessage {...messages.FillPeriodWithRecup} />
                                  </MenuItem>
                                ) : null}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                          {totalRemainingOvertimePeriod !== 0 && (
                            <td
                              className={classnames('text-right hours', {
                                'text-muted': !row.totalRemainingOvertimePeriod,
                              })}
                              onClick={toggleFunction}
                            >
                              {row.totalRemainingOvertimePeriod.minuteToTime()}
                            </td>
                          )}
                          {showRemainingOvertime && (
                            <td
                              className={classnames('text-right hours', {
                                'text-muted': !row.remainingOvertime,
                              })}
                              onClick={toggleFunction}
                            >
                              {row.remainingOvertime.minuteToTime()}
                            </td>
                          )}
                        </tr>
                        {showDetails[row.userId] ? this.renderDetails(row) : null}
                      </tbody>
                    );
                  })}
                  <tfoot>
                    <tr>
                      <td></td>
                      <th>
                        <FormattedMessage {...messages.Totals} />
                      </th>
                      {report.columns.map(col => (
                        <th
                          key={col.groupNr}
                          className={classnames(
                            'text-right hours',
                            { 'text-muted': !col.total },
                            {
                              weekend:
                                col.endDate.toISOString() === col.startDate.toISOString() &&
                                (col.endDate.isoWeekday() === 6 || col.endDate.isoWeekday() === 7),
                            },
                          )}
                        >
                          {!col.total &&
                          (col.total !== 0 ||
                            (col.totalExpected !== null &&
                              typeof col.totalExpected !== 'undefined' &&
                              col.totalExpected === 0))
                            ? null
                            : col.total.minuteToTime()}
                        </th>
                      ))}
                      <th className="text-right">{report.total.minuteToTime()}</th>
                      {totalRemainingOvertimePeriod !== 0 && (
                        <th className="text-right">
                          {totalRemainingOvertimePeriod.minuteToTime()}
                        </th>
                      )}
                      {showRemainingOvertime && (
                        <th className="text-right">
                          {report.totalRemainingOvertime.minuteToTime()}
                        </th>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
        {report && (!report.rows || !report.rows.length) && (
          <div>
            <FormattedMessage {...messages.NoDataForReport} />
          </div>
        )}
        {!report && <LoadingAnimation />}
        {showRegistrationModal && (
          <RegistrationModal
            {...showRegistrationModal}
            show
            onSave={() =>
              this.setState({
                showRegistrationModal: null,
              })
            }
            onClose={() =>
              this.setState({
                showRegistrationModal: null,
              })
            }
          />
        )}
        {showFillGapsRegistrationModal && (
          <FillGapsRegistrationModal
            {...showFillGapsRegistrationModal}
            show
            onSave={() =>
              this.setState({
                showFillGapsRegistrationModal: null,
              })
            }
            onClose={() =>
              this.setState({
                showFillGapsRegistrationModal: null,
              })
            }
          />
        )}

        {showBatchFillGapsRegistrationModal && (
          <BatchFillGapsRegistrationModal
            {...showBatchFillGapsRegistrationModal}
            show
            onSave={this.onBatchFillGaps}
            onClose={() =>
              this.setState({
                showBatchFillGapsRegistrationModal: null,
              })
            }
          />
        )}
      </div>
    );
  }
}

HoursPerEmployeeReportContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: stateServicePropType.isRequired,
};

export default showWhenPermission(
  'RegistrationOwn',
  'read',
)(injectStateService(injectIntl(HoursPerEmployeeReportContainer)));
